<template>
  <el-dialog v-model="visible" :title="operation?.title || '库存操作'" width="760px" :close-on-click-modal="false" :close-on-press-escape="!busy" :show-close="!busy" :before-close="close">
    <template v-if="operation">
      <el-alert v-if="operation.notice" :title="operation.notice" type="info" :closable="false" show-icon />
      <el-alert v-if="error" :title="error" type="error" :closable="false" class="message" />
      <el-alert v-if="frozen && !busy" title="提交结果尚未确认，表单已保留。重试将发送同一编号和原始内容，避免重复入账；请勿新建同一笔业务。" type="warning" :closable="false" class="message" />
      <el-form label-width="170px" class="operation-form" @submit.prevent>
        <el-form-item v-if="values.expectedVersion !== undefined" label="提交版本"><span>{{ values.expectedVersion }}</span></el-form-item>
        <el-form-item v-if="frozen?.requestId" label="待确认请求编号"><span class="request-id">{{ frozen.requestId }}</span></el-form-item>
        <AccountingFields :fields="operation.fields" :values="values" :disabled="busy || !!frozen" />
        <el-form-item v-if="operation.preview" label="确认可用量"><strong>{{ preview }}</strong></el-form-item>
      </el-form>
    </template>
    <template #footer>
      <el-button :disabled="busy" @click="close">关闭</el-button>
      <el-button type="primary" :loading="busy" @click="submit">{{ frozen ? '按原请求重试' : '确认提交' }}</el-button>
    </template>
  </el-dialog>
</template>
<script setup>
import { computed, ref } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import AccountingFields from './AccountingFields.vue';
import { accountingError, submitAccounting, submitControl } from '@/api/reagentAccounting';
import { isKnownRejection, pendingOwner, requestId, serializeFields } from '@/utils/reagentAccounting';
const emit = defineEmits(['submitted']);
const visible = ref(false), operation = ref(null), values = ref({}), frozen = ref(null), busy = ref(false), error = ref('');
let draftKey = '';
let invalidDraft = false;
const preview = computed(() => { try { return operation.value?.preview?.(values.value) || ''; } catch (e) { return e.message; } });
function open(op) {
  operation.value = op; values.value = JSON.parse(JSON.stringify(op.initial || {})); error.value = ''; frozen.value = null; invalidDraft = false;
  draftKey = `reagent-pending:${pendingOwner(localStorage.getItem('token'))}:${op.path}:${op.draftScope || op.initial?.accountId || op.initial?.deviceId || op.initial?.originalId || 'configuration'}`;
  try {
    const saved = sessionStorage.getItem(draftKey);
    if (saved) { frozen.value = JSON.parse(saved); values.value = { ...frozen.value }; }
  } catch (_) { invalidDraft = true; error.value = '无法读取此前待确认请求，请先核对流水再操作；此表单暂不能提交。'; }
  visible.value = true;
}
function close(done) { if (busy.value) return; visible.value = false; if (typeof done === 'function') done(); }
async function submit() {
  if (busy.value || invalidDraft) return;
  error.value = '';
  busy.value = true;
  try {
    if (!frozen.value) {
      const payload = { ...(operation.value.base || {}), ...serializeFields(operation.value.fields, values.value) };
      operation.value.validate?.(payload);
      if (operation.value.confirm) await ElMessageBox.confirm(operation.value.confirm, operation.value.title, { type: 'warning', confirmButtonText: '确认提交', cancelButtonText: '返回检查' });
      if (operation.value.idempotent !== false) payload.requestId = requestId();
      // Persist before sending so a reload or lost response can retry the same payload.
      sessionStorage.setItem(draftKey, JSON.stringify(payload)); frozen.value = payload;
    }
    busy.value = true;
    const result = operation.value.submit ? await operation.value.submit(frozen.value)
      : operation.value.control ? await submitControl(frozen.value) : await submitAccounting(operation.value.path, frozen.value);
    const payload = frozen.value;
    sessionStorage.removeItem(draftKey); frozen.value = null; visible.value = false;
    ElMessage.success('操作已保存'); emit('submitted', { path: operation.value.path, data: result.data, payload });
  } catch (e) {
    if (e === 'cancel' || e === 'close') return;
    error.value = accountingError(e);
    if (isKnownRejection(e)) { sessionStorage.removeItem(draftKey); frozen.value = null; }
  } finally { busy.value = false; }
}
defineExpose({ open });
</script>
<style scoped>.message { margin-top: 12px; } .operation-form { margin-top: 20px; max-height: 60vh; overflow: auto; padding-right: 12px; } .request-id { word-break:break-all; }</style>
